<template>
	<v-container v-if="showNotice && noticeMessage">
		<div class="notice-body">
			<img :src="InfoIconDark" alt="System Info" />
			<div class="w-100 d-flex g-2 flex-column flex-sm-row align-sm-center justify-space-between">
				<div class="d-flex flex-wrap">
					<p class="mr-2" v-html="noticeMessage"></p>
				</div>
				<v-btn
					variant="outlined"
					size="small"
					@click="dismissNotice"
					class="secondary"
					:disabled="isDismissing"
				>
					Dismiss
				</v-btn>
			</div>
		</div>
	</v-container>
</template>

<script lang="ts" setup>
// ====---------------====
// 🚀 Imports
// ====---------------====
import { onMounted, ref, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { session } from '@/utils/utils.js'
import { useNoticeStore } from '@/stores/userNotice/userNotice.ts'
import { sanitizeContent } from '@/utils/sanitization'
import InfoIconDark from '@/assets/logo-info-icon-dark.svg'

const route = useRoute()
const NOTICE_DISMISSED_KEY = 'user_notice_dismissed'

// ====---------------====
// 💡 Data
// ====---------------====
const showNotice = ref(false)
const noticeMessage = ref<string | null>(null)
const isDismissing = ref(false)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// ====---------------====
// 🛠 Methods
// ====---------------====
function dismissNotice() {
	isDismissing.value = true
	try {
		session.set(NOTICE_DISMISSED_KEY, true)
		showNotice.value = false
	} finally {
		isDismissing.value = false
	}
}

/**
 * Sanitizes HTML content from internal API
 * Prevents XSS while allowing safe formatting and links
 *
 * @param html - Raw HTML string from API
 * @returns Sanitized HTML safe for rendering
 */
function sanitizeNoticeHtml(html: string): string {
	return sanitizeContent(html)
}

async function getUserNotice() {
	try {
		const isDismissed = session.get(NOTICE_DISMISSED_KEY)
		if (isDismissed) {
			showNotice.value = false
			return
		}

		const userNotice = await useNoticeStore().getStore()
		if (userNotice.loaded && userNotice.state) {
			noticeMessage.value = sanitizeNoticeHtml(userNotice.state)
			showNotice.value = true
		} else {
			showNotice.value = false
		}
	} catch (error) {
		console.error('Failed to load user notice banner:', error)
		showNotice.value = false
	}
}

/**
 * Debounced version of getUserNotice to prevent excessive calls
 * during rapid route changes
 */
function debouncedGetUserNotice() {
	if (debounceTimer) {
		clearTimeout(debounceTimer)
	}

	debounceTimer = setTimeout(() => {
		getUserNotice()
		debounceTimer = null
	}, 300)
}

// ====---------------====
// 🌄 Lifecycle
// ====---------------====
onMounted(() => {
	getUserNotice()
})

watch(route, () => {
	debouncedGetUserNotice()
})

onBeforeUnmount(() => {
	if (debounceTimer) {
		clearTimeout(debounceTimer)
		debounceTimer = null
	}
})
</script>

<style lang="scss" scoped>
.v-container {
	max-width: initial;

	margin-bottom: 0;
	padding-block: 0.2rem;
	border-bottom: 1px solid $neutral-300;
	background: $primary-950;
}

.notice-body {
	width: 100%;
	max-width: 1280px;

	display: flex;
	align-items: center;
	flex-direction: row;
	gap: 16px;

	padding: 8px 0;
	margin: 0 auto;

	color: $neutral-0;
	font-size: 0.875rem;

	p {
		color: $neutral-0;
	}
}

@media only screen and (max-width: $small-glass) {
	section {
		align-items: flex-start;
	}

	.v-container {
		padding: 0 !important;
	}
}
</style>
